/**
 * Client-side image resizer using Canvas API
 * Resizes images that exceed configured dimensions before upload
 */

/**
 * Resize a single image if it exceeds the size limit
 * @param {File} file - The image file to potentially resize
 * @param {Object} config - Upload configuration
 * @returns {Promise<{file: File, wasResized: boolean, originalSize: number, newSize: number}>}
 */
export async function resizeImageIfNeeded(file, config) {
  const {
    enableClientResize = true,
    clientResizeMaxWidth = 1920,
    clientResizeMaxHeight = 1080,
    clientResizeQuality = 0.85,
    maxFileSizeMB = 2,
  } = config;

  const originalSize = file.size;

  // Skip if resizing is disabled
  if (!enableClientResize) {
    return { file, wasResized: false, originalSize, newSize: originalSize };
  }

  // Only process image files
  if (!file.type.startsWith('image/')) {
    return { file, wasResized: false, originalSize, newSize: originalSize };
  }

  // Skip if file is already under the size limit
  const maxBytes = maxFileSizeMB * 1024 * 1024;
  if (file.size <= maxBytes) {
    return { file, wasResized: false, originalSize, newSize: originalSize };
  }

  return new Promise(resolve => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        // First, scale down if dimensions exceed maximums
        if (width > clientResizeMaxWidth) {
          width = clientResizeMaxWidth;
          height = Math.round(width / aspectRatio);
        }
        if (height > clientResizeMaxHeight) {
          height = clientResizeMaxHeight;
          width = Math.round(height * aspectRatio);
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.warn('Failed to get canvas context for resizing:', file.name);
          resolve({ file, wasResized: false, originalSize, newSize: originalSize });
          return;
        }

        // Use better quality settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        try {
          ctx.drawImage(img, 0, 0, width, height);
        } catch (drawError) {
          console.warn('Failed to draw image for resizing:', file.name, drawError);
          resolve({ file, wasResized: false, originalSize, newSize: originalSize });
          return;
        }

        // Try progressively lower quality if still too large
        const tryCompress = quality => {
          canvas.toBlob(
            blob => {
              if (blob) {
                // If still too large and quality can be reduced, try again
                if (blob.size > maxBytes && quality > 0.3) {
                  tryCompress(quality - 0.1);
                  return;
                }

                // Create new File with original name
                const resizedFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, '.jpg'), // Ensure .jpg extension
                  { type: 'image/jpeg', lastModified: Date.now() },
                );

                console.log(`Image resized: ${file.name}`, {
                  originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
                  newSize: `${(resizedFile.size / 1024 / 1024).toFixed(2)}MB`,
                  reduction: `${(((file.size - resizedFile.size) / file.size) * 100).toFixed(0)}%`,
                  originalDimensions: `${img.width}x${img.height}`,
                  newDimensions: `${width}x${height}`,
                  quality: quality.toFixed(2),
                });

                resolve({
                  file: resizedFile,
                  wasResized: true,
                  originalSize: file.size,
                  newSize: resizedFile.size,
                });
              } else {
                // Blob creation failed, return original
                resolve({ file, wasResized: false, originalSize, newSize: originalSize });
              }
            },
            'image/jpeg',
            quality,
          );
        };

        tryCompress(clientResizeQuality);
      };

      img.onerror = () => {
        // Image load failed, return original
        console.warn(`Failed to load image for resizing: ${file.name}`);
        resolve({ file, wasResized: false, originalSize, newSize: originalSize });
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      // FileReader failed, return original
      console.warn(`Failed to read file for resizing: ${file.name}`);
      resolve({ file, wasResized: false, originalSize, newSize: originalSize });
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Process multiple files, resizing as needed
 * @param {File[]} files - Array of image files
 * @param {Object} config - Upload configuration
 * @returns {Promise<{files: File[], resizedCount: number, totalSaved: number}>}
 */
export async function resizeImagesIfNeeded(files, config) {
  const results = await Promise.all(files.map(file => resizeImageIfNeeded(file, config)));

  const resizedCount = results.filter(r => r.wasResized).length;
  const totalSaved = results.reduce((sum, r) => sum + (r.originalSize - r.newSize), 0);

  if (resizedCount > 0) {
    console.log(
      `Resized ${resizedCount} of ${files.length} images, saved ${(totalSaved / 1024 / 1024).toFixed(2)}MB`,
    );
  }

  return {
    files: results.map(r => r.file),
    resizedCount,
    totalSaved,
  };
}
